import React from "react";
import { motion } from "framer-motion";
import { Container } from "../ui";
import { cn } from "../../utils/cn";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../utils/appLinks";

export interface HeroProps {
  className?: string;
}

const Hero: React.FC<HeroProps> = ({ className }) => {
  return (
    <section
      id="hero"
      className={cn(
        "relative bg-gradient-to-br from-[#2E1654] via-[#3B1F6D] to-[#59359B] pt-24 sm:pt-28 lg:pt-32 pb-0 overflow-hidden text-white",
        className
      )}
    >
      {/* Fine line grid + top sheen for depth */}
      <div className="absolute inset-0 bg-line-grid pointer-events-none z-0" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none" aria-hidden="true" />

      {/* Decorative 4x5 Dot Matrix on Top-Left */}
      <div className="absolute top-24 left-6 sm:left-12 pointer-events-none opacity-25 z-0">
        <div className="grid grid-cols-5 gap-3">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-purple-200" />
          ))}
        </div>
      </div>

      {/* Glossy 3D Lavender Sphere on Far-Left Edge (Visible on sm+) */}
      <div
        className="absolute top-[32%] -left-6 sm:-left-8 w-16 h-32 sm:w-20 sm:h-36 rounded-r-full bg-gradient-to-r from-purple-400/30 via-purple-300/50 to-purple-200/60 blur-[1px] shadow-[inset_-4px_0_12px_rgba(255,255,255,0.4)] pointer-events-none z-0 hidden sm:block"
        aria-hidden="true"
      />

      {/* Ambient Lighting Glows */}
      <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[500px] h-[450px] bg-purple-600/25 rounded-full blur-3xl pointer-events-none" />

      {/* Subtle Curved Wave in Top-Right Background */}
      <div className="absolute top-0 right-0 w-[600px] h-[500px] pointer-events-none overflow-hidden opacity-35 z-0">
        <svg viewBox="0 0 600 500" fill="none" className="w-full h-full">
          <path
            d="M 600,0 L 150,0 C 250,180 380,300 600,380 Z"
            fill="url(#topRightWave)"
          />
          <defs>
            <linearGradient id="topRightWave" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#6366F1" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Subtle Curved Hill in Bottom-Left Background */}
      <div className="absolute bottom-0 left-0 w-full max-w-xl h-28 pointer-events-none opacity-15 z-0">
        <svg viewBox="0 0 600 120" fill="none" className="w-full h-full">
          <path
            d="M0 120 C180 30 380 50 600 120 L0 120 Z"
            fill="url(#bottomHillGrad)"
          />
          <defs>
            <linearGradient id="bottomHillGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#C4B5FD" />
              <stop offset="100%" stopColor="#818CF8" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <Container size="xl" padding="md" className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          {/* Left Column: Headline, Subtitle, & Download Actions */}
          <motion.div
            className="lg:col-span-6 text-center lg:text-left pt-6 sm:pt-10 pb-8 sm:pb-12 lg:pb-16"
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="relative inline-block text-center lg:text-left">
              {/* Floating Translucent Gift Box Accent behind 'Earn Rewards.' */}
              <motion.div
                className="absolute -left-6 sm:-left-10 top-[55%] -translate-y-1/2 -z-10 pointer-events-none hidden sm:block"
                animate={{ y: [0, -6, 0], rotate: [-10, -6, -10] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg flex items-center justify-center p-3.5">
                  <svg
                    className="w-full h-full text-purple-200/70"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v13m0-13V4.5a2.5 2.5 0 115 0v3.5m-5 0V4.5a2.5 2.5 0 10-5 0v3.5m5 0h8.5a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5H12m0-6H3.5A1.5 1.5 0 002 9.5v3A1.5 1.5 0 003.5 14H12m0 0v7m-8.5-7v6a2 2 0 002 2h13a2 2 0 002-2v-6"
                    />
                  </svg>
                </div>
              </motion.div>

              <h1 className="text-3xl sm:text-5xl lg:text-[58px] xl:text-[62px] font-black text-white tracking-tight leading-[1.10]">
                Discover Local.
                <br />
                Save More.
                <br />
                <span className="text-[#FFC727] drop-shadow-[0_2px_14px_rgba(255,199,39,0.35)]">
                  Earn Rewards.
                </span>
              </h1>
            </div>

            <p className="text-sm sm:text-base lg:text-[19px] text-purple-100/90 max-w-xl mx-auto lg:mx-0 mt-4 sm:mt-6 leading-relaxed font-normal">
              Find nearby offers, redeem in-store and earn coins instantly.
            </p>

            {/* App Store and Google Play Download Badges */}
            <div
              id="hero-download"
              className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 pt-6 sm:pt-10"
            >
              {/* App Store Badge */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-badge sheen inline-flex items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 group"
              >
                <svg
                  className="w-6 sm:w-7 h-6 sm:h-7 fill-current text-white group-hover:text-gray-100 transition-colors"
                  viewBox="0 0 170 170"
                >
                  <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.66-7.83-11.88-14.34-6.3-9.88-11.1-20.73-14.4-32.55-3.3-11.83-4.96-22.9-4.96-33.24 0-14.99 3.82-27.27 11.45-36.83 7.64-9.56 17.2-14.44 28.69-14.65 4.35 0 9.4 1.13 15.15 3.39 5.76 2.26 9.61 3.44 11.55 3.53 1.52 0 5.48-1.25 11.88-3.75 6.4-2.5 11.75-3.6 16.06-3.3 11.85.64 21.36 4.67 28.53 12.09-10.43 6.31-15.54 15.01-15.34 26.1.2 8.7 3.52 16.03 9.96 21.99 6.44 5.96 14.15 9.35 23.13 10.17-2.17 6.53-4.89 13.06-8.16 19.59zM119.22 33.15c0-7.22 2.6-13.9 7.8-20.04 5.2-6.14 11.66-10.18 19.38-12.11.22 1.3.33 2.45.33 3.44 0 7.12-2.73 13.94-8.19 20.45-5.46 6.51-12.07 10.37-19.82 11.58-.33-1.09-.5-2.19-.5-3.32z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] leading-tight font-medium text-gray-300">
                    Download on the
                  </div>
                  <div className="text-xs sm:text-sm font-bold leading-snug tracking-tight text-white">
                    App Store
                  </div>
                </div>
              </a>

              {/* Google Play Store Badge */}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-badge sheen inline-flex items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/30 group"
              >
                <svg className="w-5 sm:w-6 h-5 sm:h-6" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M3.609 1.814L13.793 12 3.61 22.186c-.352-.353-.57-.843-.57-1.386V3.2c0-.543.218-1.033.57-1.386z"
                  />
                  <path
                    fill="#34A853"
                    d="M17.202 8.591l-3.409 3.409 3.409 3.409 3.86-2.228c.703-.406.703-1.078 0-1.484l-3.86-3.106z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M13.793 12L3.609 1.814c.264-.265.61-.433.998-.433.393 0 .762.176 1.026.471l11.569 6.739-3.409 3.409z"
                  />
                  <path
                    fill="#EA4335"
                    d="M13.793 12l3.409 3.409-11.569 6.739c-.264.295-.633.471-1.026.471-.388 0-.734-.168-.998-.433L13.793 12z"
                  />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] leading-tight font-medium text-gray-300">
                    Get it on
                  </div>
                  <div className="text-xs sm:text-sm font-bold leading-snug tracking-tight text-white">
                    Google Play
                  </div>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right Column: Layered Phones & Floating 3D Elements */}
          <div className="lg:col-span-6 relative flex justify-center lg:justify-end items-end self-end pt-6 lg:pt-0">
            {/* Relative Canvas Anchored to Bottom */}
            <div className="relative w-full max-w-[340px] sm:max-w-[440px] md:max-w-[500px] lg:max-w-[560px] xl:max-w-[600px] flex items-end justify-center lg:justify-end mx-auto lg:mx-0">
              
              {/* Floating 3D Coin with Glowing Orbit Ring (Visible on sm+) */}
              <motion.div
                className="absolute -top-4 sm:-top-6 right-4 sm:right-14 z-20 pointer-events-none hidden sm:block"
                animate={{ y: [0, -10, 0], rotate: [0, 6, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="relative">
                  <img
                    src="/hero_coin_only.png"
                    alt="Gold Coin"
                    className="w-14 sm:w-20 h-14 sm:h-20 object-contain drop-shadow-[0_12px_24px_rgba(255,199,39,0.35)]"
                  />
                  {/* Luminous Orbit Ring */}
                  <div className="absolute -inset-3 sm:-inset-6 rounded-full border border-white/40 scale-x-150 -rotate-12 pointer-events-none shadow-[0_0_15px_rgba(255,255,255,0.2)]" />
                </div>
              </motion.div>

              {/* Floating Translucent 3D Heart Tile (Visible on sm+) */}
              <motion.div
                className="absolute top-16 sm:top-24 right-0 sm:right-2 z-20 pointer-events-none hidden sm:block"
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              >
                <img
                  src="/hero_heart_clean.png"
                  alt="Saved Offer Badge"
                  className="w-10 sm:w-14 h-12 sm:h-16 object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.3)]"
                />
              </motion.div>

              {/* Soft spotlight behind the phones */}
              <div
                className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 w-[85%] aspect-square rounded-full bg-[radial-gradient(circle,rgba(167,139,250,0.45)_0%,rgba(124,58,237,0.18)_40%,transparent_70%)] blur-2xl pointer-events-none z-0"
                aria-hidden="true"
              />

              {/* Decorative 4x4 Dot Matrix on Bottom Right behind Phone */}
              <div className="absolute bottom-8 right-2 sm:right-4 pointer-events-none opacity-20 z-0">
                <div className="grid grid-cols-4 gap-2.5">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <div key={i} className="w-1.5 h-1.5 rounded-full bg-purple-200" />
                  ))}
                </div>
              </div>

              {/* Main Dual Phones Composition (100% Transparent Background).
                  Box keeps the original 544x467 artwork proportions so the hero height stays the same. */}
              <motion.div
                className="relative z-10 w-full aspect-[544/467] flex items-end justify-center lg:justify-end"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
              >
                <img
                  src="/hero-phones.webp"
                  srcSet="/hero-phones-sm.webp 820w, /hero-phones.webp 1544w"
                  sizes="(min-width: 1280px) 600px, (min-width: 1024px) 560px, (min-width: 768px) 500px, (min-width: 640px) 440px, 92vw"
                  width={1544}
                  height={1060}
                  alt="SetGo customer and partner app screens"
                  decoding="async"
                  className="w-full h-full object-contain object-bottom drop-shadow-[0_30px_45px_rgba(10,0,30,0.5)] block"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export { Hero };
export default Hero;
